import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import MotionMedia from "../components/MotionMedia";
import { ONGOING_PROJECTS } from "../data/ongoingProjects";
import { COMPANY } from "../content";

const SITE = "https://timefurniture.ae";

export function OngoingBadges({ testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      <span className="inline-block bg-[#6E1F2C] text-[#FAF5EC] text-[9px] md:text-[10px] tracking-[0.2em] uppercase px-2.5 py-1">
        Ongoing project
      </span>
      <span className="inline-block bg-[#1C110C]/80 text-[#EDE1C8] text-[9px] md:text-[10px] tracking-[0.2em] uppercase px-2.5 py-1">
        Design visualization
      </span>
    </div>
  );
}

export default function OngoingProjects() {
  usePageMeta({
    title: "Ongoing Projects | Time Furniture Dubai & UAE",
    description:
      "Six ongoing interior and fit-out projects by Time Furniture — residential villas, a Palm Jumeirah residence, a commercial golf lounge and a desert hospitality concept in Sharjah. Selected design visualizations.",
    canonical: `${SITE}/ongoing-projects`,
    ogImage: `${SITE}/media/projects/murooj-1.jpg`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Ongoing Projects", item: `${SITE}/ongoing-projects` },
        ],
      },
    ],
  });

  return (
    <div data-testid="ongoing-projects-page" className="bg-[#FAF5EC]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <Breadcrumbs padTop="pt-5 md:pt-8" items={[{ label: "Home", to: "/" }, { label: "Ongoing Projects" }]} />

        <div className="mt-5 md:mt-7 max-w-3xl">
          <p className="eyebrow mb-5">From Our Ongoing Projects</p>
          <h1 data-testid="ongoing-projects-h1" className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1C110C]">
            Ongoing <em className="text-[#6E1F2C] not-italic">projects</em>.
          </h1>
          <p className="mt-6 text-[#3E2A22] leading-[1.85] text-base md:text-lg">
            Six ongoing projects — from residential villas and a Palm Jumeirah
            residence to a commercial golf lounge and a desert hospitality
            concept in Sharjah.
          </p>
          <p className="mt-4 text-sm text-[#7A6B5F] leading-relaxed" data-testid="ongoing-projects-disclaimer">
            All imagery on these pages is <strong>selected design visualizations</strong> — not
            completed-site photography. Completed work lives in our{" "}
            <Link to="/portfolio" className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]">portfolio</Link>.
          </p>
        </div>

        <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {ONGOING_PROJECTS.map((p) => (
            <div key={p.slug} data-testid={`ongoing-card-${p.slug}`} className="group">
              {/* Media controls are siblings of the link — Play never navigates */}
              <div className="relative">
                <MotionMedia
                  mediaKey={p.videoKey}
                  alt={p.images[0].alt}
                  mode="card"
                  aspectClass="aspect-[16/10]"
                  testid={`ongoing-media-${p.slug}`}
                  imgClassName="transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
                />
                <div className="absolute top-3 left-3 z-10 pointer-events-none">
                  <OngoingBadges testid={`ongoing-badges-${p.slug}`} />
                </div>
              </div>
              <Link
                to={`/ongoing-projects/${p.slug}`}
                data-testid={`ongoing-card-link-${p.slug}`}
                className="block mt-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A963]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C]">
                      {p.location ? `${p.category} · ${p.location}` : p.category}
                    </p>
                    <h2 className="mt-2 font-serif-display text-2xl md:text-[26px] text-[#1C110C] leading-snug group-hover:text-[#6E1F2C] transition-colors">
                      {p.title}
                    </h2>
                  </div>
                  <ArrowRight size={18} className="mt-2 shrink-0 text-[#6E1F2C] opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                </div>
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-20 border-t border-[#6E1F2C]/15 pt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C]">
              Planning a project of your own?
            </h2>
            <p className="mt-2 text-sm md:text-base text-[#3E2A22]">
              One contract, one accountable team — since 1993.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              data-testid="ongoing-projects-cta-quote"
              className="inline-flex items-center justify-center gap-3 bg-[#6E1F2C] text-[#FAF5EC] px-8 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#56151F] transition-colors"
            >
              Start Your Project
            </Link>
            <a
              href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent("Hi Time Furniture, I saw your ongoing projects on the website and would like to discuss a similar project.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ongoing-projects-cta-whatsapp"
              className="inline-flex items-center justify-center gap-3 border border-[#6E1F2C] text-[#6E1F2C] px-8 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors"
            >
              Enquire on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
